// Day Close API functions for day_close_summary table integration
import fetchData from '../functions/fetchData';
import axios from 'axios';
import { getHeaders } from '../utility/getHeader';

// Check if day is already closed for a specific date and user
export const checkDayCloseStatus = async (date, userId) => {
  try {
    const whereCondition = `close_date=${date}&closed_by=${userId}&status=closed`;
    
    const result = await fetchData('day_close_summary', null, 'id', whereCondition);
    
    if (result && result.length > 0) {
      return {
        isClosed: true,
        closeData: result[0],
        message: 'Day has already been closed'
      };
    }
    
    return {
      isClosed: false,
      closeData: null,
      message: 'Day is still open'
    };
  } catch (error) {
    console.error('Error checking day close status:', error);
    return {
      isClosed: false,
      closeData: null,
      message: 'Error checking status',
      error: error.message
    };
  }
};

// Save day close summary to database
export const saveDayCloseSummary = async (dayCloseData) => {
  try {
    console.log('Saving day close data to database:', dayCloseData);
    
    // First check if entry already exists for this date
    const existing = await checkDayCloseStatus(dayCloseData.close_date, dayCloseData.closed_by);
    
    if (existing.isClosed) {
      throw new Error('Day has already been closed for this date');
    }
    
    // Save new day close summary using axios.post
    console.log('Saving day close data to database:', dayCloseData);
    
    const response = await axios.post('/insertdata/day_close_summary', dayCloseData, getHeaders());
    
    if (response.data && response.data.success) {
      return {
        success: true,
        data: response.data,
        message: 'Day close summary saved successfully'
      };
    } else {
      throw new Error('Failed to save day close summary');
    }
    
  } catch (error) {
    console.error('Error saving day close summary:', error);
    return {
      success: false,
      data: null,
      message: 'Failed to save day close summary',
      error: error.message
    };
  }
};

// Get day close summary for a specific date
export const getDayCloseSummary = async (date, userId = null) => {
  try {
    let whereCondition = `close_date=${date}`;
    if (userId) {
      whereCondition += `&closed_by=${userId}`;
    }
    
    const result = await fetchData('day_close_summary', null, 'id DESC', whereCondition);
    
    return {
      success: true,
      data: result || [],
      message: 'Day close summary retrieved successfully'
    };
    
  } catch (error) {
    console.error('Error getting day close summary:', error);
    return {
      success: false,
      data: [],
      message: 'Failed to get day close summary',
      error: error.message
    };
  }
};

// Update existing day close summary
export const updateDayCloseSummary = async (id, updateData) => {
  try {
    // Update day close summary using axios.put
    console.log('Updating day close data for id', id, ':', updateData);
    
    const response = await axios.put(`/updatedata/day_close_summary/${id}`, updateData, getHeaders());
    
    if (response.data && response.data.success) {
      return {
        success: true,
        data: response.data,
        message: 'Day close summary updated successfully'
      };
    } else {
      throw new Error('Failed to update day close summary');
    }
    
  } catch (error) {
    console.error('Error updating day close summary:', error);
    return {
      success: false,
      data: null,
      message: 'Failed to update day close summary',
      error: error.message
    };
  }
};

// Validate cashier login - prevent login if day is not closed
export const validateCashierLogin = async (userId) => {
  try {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = yesterday.toISOString().split('T')[0];
    
    // Check if yesterday was closed
    const yesterdayStatus = await checkDayCloseStatus(yesterdayStr, userId);
    
    // If cashier worked yesterday and didn't close, prevent login
    if (!yesterdayStatus.isClosed) {
      // Check if there were any transactions yesterday
      const whereCondition = `dte=${yesterdayStr}&userid=${userId}`;
      
      const transactions = await fetchData('collection', null, 'id DESC', whereCondition);
      
      if (transactions && transactions.length > 0) {
        return {
          canLogin: false,
          message: `Please close the counter for ${yesterdayStr} before logging in today. Contact administrator if needed.`,
          pendingDate: yesterdayStr
        };
      }
    }
    
    // Check if today is already closed
    const today = new Date().toISOString().split('T')[0];
    const todayStatus = await checkDayCloseStatus(today, userId);
    
    if (todayStatus.isClosed) {
      return {
        canLogin: false,
        message: `Counter has already been closed for today (${today}). Please contact administrator.`,
        pendingDate: today
      };
    }
    
    return {
      canLogin: true,
      message: 'Login allowed',
      pendingDate: null
    };
    
  } catch (error) {
    console.error('Error validating cashier login:', error);
    return {
      canLogin: true, // Allow login on error to prevent system lock-out
      message: 'Unable to validate login status',
      error: error.message
    };
  }
};

// Get monthly day close summary for reports
export const getMonthlyDayCloseSummary = async (year, month, userId = null) => {
  try {
    const startDate = `${year}-${month.toString().padStart(2, '0')}-01`;
    const endDate = `${year}-${month.toString().padStart(2, '0')}-31`;
    
    let whereCondition = `close_date>=${startDate}&close_date<=${endDate}`;
    
    if (userId) {
      whereCondition += `&closed_by=${userId}`;
    }
    
    const result = await fetchData('day_close_summary', null, 'close_date ASC', whereCondition);
    
    return {
      success: true,
      data: result || [],
      message: 'Monthly summary retrieved successfully'
    };
    
  } catch (error) {
    console.error('Error getting monthly summary:', error);
    return {
      success: false,
      data: [],
      message: 'Failed to get monthly summary',
      error: error.message
    };
  }
};

// Check if cashier has already closed counter for a specific date
export const checkCashierSummaryStatus = async (date, cashierId) => {
  try {
    console.log(`Checking cashier summary status for cashier ${cashierId} on ${date}`);
    
    const whereCondition = `cashier_id=${cashierId}&date=${date}`;
    const result = await fetchData('cashier_summaries', null, 'id', whereCondition);
    
    if (result && result.length > 0) {
      const summary = result[0];
      return {
        isClosed: summary.counter_status === 'closed',
        data: summary,
        message: summary.counter_status === 'closed' ? 'Counter already closed for this date' : 'Counter is open'
      };
    }
    
    return {
      isClosed: false,
      data: null,
      message: 'No record found - counter can be closed'
    };
    
  } catch (error) {
    console.error('Error checking cashier summary status:', error);
    return {
      isClosed: false,
      data: null,
      error: error.message,
      message: 'Error checking status'
    };
  }
};

// Save cashier summary to database
export const saveCashierSummary = async (cashierData) => {
  try {
    console.log('Saving cashier summary data to database:', cashierData);
    
    // First check if entry already exists for this cashier and date
    const whereCondition = `cashier_id=${cashierData.cashier_id}&date=${cashierData.date}`;
    const existingData = await fetchData('cashier_summaries', null, 'id', whereCondition);
    
    if (existingData && existingData.length > 0) {
      throw new Error('Counter has already been closed for this cashier and date');
    }
    
    // Save new cashier summary using axios.post
    const response = await axios.post('/insertdata/cashier_summaries', cashierData, getHeaders());
    
    console.log('API Response:', response.data);
    
    // Check for successful response - handle different response formats
    if (response.data) {
      // If response has success property
      if (response.data.success === true || response.data.success === 1) {
        return {
          success: true,
          data: response.data,
          message: 'Cashier summary saved successfully'
        };
      }
      // If response doesn't have success property but has data (some APIs return data directly)
      else if (response.data.insertId || response.data.affectedRows || response.status === 200) {
        return {
          success: true,
          data: response.data,
          message: 'Cashier summary saved successfully'
        };
      }
    }
    
    // If we get here, the save might have failed
    console.warn('Unexpected response format:', response.data);
    throw new Error(response.data?.message || 'Failed to save cashier summary - unexpected response format');
    
  } catch (error) {
    console.error('Error saving cashier summary:', error);
    return {
      success: false,
      message: error.message || 'Failed to save cashier summary',
      error: error.message
    };
  }
};

// Get last close date from API
export const getLastCloseDate = async () => {
  try {
    console.log('Fetching last close date from API...');
    
    const response = await axios.get('/last-close-date', getHeaders());
    
    if (response.data && response.data.success) {
      return {
        success: true,
        currentBillingDate: response.data.close_date || response.data.data?.dte || response.data.currentBillingDate,
        data: response.data,
        message: 'Last close date retrieved successfully'
      };
    } else {
      // Fallback to current date if API doesn't return expected format
      return {
        success: true,
        currentBillingDate: new Date().toISOString().split('T')[0],
        data: response.data,
        message: 'Using fallback date'
      };
    }
    
  } catch (error) {
    console.error('Error fetching last close date:', error);
    return {
      success: false,
      currentBillingDate: new Date().toISOString().split('T')[0],
      error: error.message,
      message: 'Error fetching date, using fallback'
    };
  }
};

// Check cashier counter status for current billing date
export const checkCashierCounterStatus = async (cashierId) => {
  try {
    console.log(`Checking counter status for cashier ${cashierId}`);
    
    // Get current billing date from sale_date_setup table
    // We'll use the /last-close-date API endpoint similar to ManualPayment
    const response = await axios.get('/last-close-date', getHeaders());
    
    let currentBillingDate;
    if (response.data && response.data.success) {
      // Parse the response similar to ManualPayment
      currentBillingDate = response.data.close_date || response.data.data?.dte;
    }
    
    if (!currentBillingDate) {
      // Fallback to today's date if API fails
      currentBillingDate = new Date().toISOString().split('T')[0];
    }
    
    console.log(`Current billing date: ${currentBillingDate}`);
    
    // Check if cashier has already closed counter for current billing date
    const cashierStatus = await checkCashierSummaryStatus(currentBillingDate, cashierId);
    
    return {
      success: true,
      currentBillingDate: currentBillingDate,
      isClosed: cashierStatus.isClosed,
      cashierSummary: cashierStatus.data,
      message: cashierStatus.message,
      canLogin: !cashierStatus.isClosed // Can login only if counter is not closed
    };
    
  } catch (error) {
    console.error('Error checking cashier counter status:', error);
    return {
      success: false,
      currentBillingDate: new Date().toISOString().split('T')[0],
      isClosed: false,
      cashierSummary: null,
      canLogin: true, // Allow login on error to prevent lockout
      error: error.message,
      message: 'Error checking counter status'
    };
  }
};

const dayCloseApiUtils = {
  checkDayCloseStatus,
  checkCashierSummaryStatus,
  checkCashierCounterStatus,
  saveDayCloseSummary,
  saveCashierSummary,
  getDayCloseSummary,
  getLastCloseDate,
  updateDayCloseSummary,
  validateCashierLogin,
  getMonthlyDayCloseSummary
};

export default dayCloseApiUtils;